import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatformTenant, type UserRole } from '../../contexts/PlatformTenantContext';
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus';
import { Loader2, AlertTriangle } from 'lucide-react';

// Re-export for convenience
export type { UserRole } from '../../contexts/PlatformTenantContext';

const ROLE_HIERARCHY: Record<UserRole, number> = {
  anonymous: 0,
  user: 1,
  customer_admin: 2,
  platform_admin: 3,
  super_admin: 4,
};

interface RequireAuthProps {
  children: React.ReactNode;
  /**
   * Minimum role required to access this route
   * If not specified, any authenticated user can access
   */
  minRole?: UserRole;
  /**
   * Specific roles that can access this route
   * Takes precedence over minRole if both specified
   */
  allowedRoles?: UserRole[];
  /**
   * Redirect path if not authorized (default: /login)
   */
  redirectTo?: string;
  /**
   * Show loading state while checking auth
   */
  showLoading?: boolean;
  /**
   * Require onboarding to be completed before accessing route
   * Admins are automatically exempt from this requirement
   */
  requireOnboardingComplete?: boolean;
}

/**
 * Protected Route wrapper with Role-Based Access Control
 * 
 * Usage:
 * ```tsx
 * <Route path="/admin/*" element={
 *   <RequireAuth allowedRoles={['super_admin', 'platform_admin']}>
 *     <AdminRoutes />
 *   </RequireAuth>
 * } />
 * ```
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  minRole,
  allowedRoles,
  redirectTo = '/login',
  showLoading = true,
  requireOnboardingComplete = false,
}) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { userRole, loading: tenantLoading, roleLoading } = usePlatformTenant();
  const { 
    isComplete: onboardingComplete, 
    isExempt: onboardingExempt,
    isLoading: onboardingLoading 
  } = useOnboardingStatus();
  const location = useLocation();

  // Consolidated loading check - show single loading state instead of sequential messages
  const isStillLoading = authLoading || (user && (roleLoading || tenantLoading)) || 
    (requireOnboardingComplete && user && onboardingLoading);
  
  if (isStillLoading) {
    if (showLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return null;
  }

  // If user exists but userRole is still null (shouldn't happen after roleLoading is false)
  if (user && !userRole) {
    if (showLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-foreground" />
            <p className="text-muted-foreground">Verifying role...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname, returnTo: location.pathname + location.search }} 
        replace 
      />
    );
  }

  // Get effective user role
  const effectiveRole: UserRole = userRole || 'user';

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    // Check if user's role is in the allowed list
    if (!allowedRoles.includes(effectiveRole)) {
      return <Navigate to="/unauthorized" state={{ requiredRoles: allowedRoles }} replace />;
    }
  } else if (minRole) {
    // Check if user's role meets minimum requirement
    const userRoleLevel = ROLE_HIERARCHY[effectiveRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0;
    
    if (userRoleLevel < requiredLevel) {
      return <Navigate to="/unauthorized" state={{ minRole }} replace />;
    }
  }

  // Check onboarding completion if required (admins are exempt)
  if (requireOnboardingComplete && !onboardingComplete && !onboardingExempt) {
    return (
      <div className="flex items-center justify-center py-12 p-4">
        <div className="max-w-md w-full bg-card rounded shadow-md p-8 text-center space-y-6">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded bg-warning/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-warning" />
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              Onboarding Required
            </h2>
            <p className="text-muted-foreground">
              Please complete the onboarding process before accessing the portal.
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/onboarding'}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors font-medium"
          >
            Complete Onboarding
          </button>
        </div>
      </div>
    );
  }

  // Authorized - render children
  return <>{children}</>;
};

/**
 * Hook to check if current user has required role
 */
export function useHasRole(role: UserRole): boolean {
  const { userRole } = usePlatformTenant();
  const effectiveRole: UserRole = userRole || 'user';
  
  return ROLE_HIERARCHY[effectiveRole] >= ROLE_HIERARCHY[role];
}

/**
 * Hook to check if current user has any of the specified roles
 */
export function useHasAnyRole(roles: UserRole[]): boolean {
  const { userRole } = usePlatformTenant();
  const effectiveRole: UserRole = userRole || 'user';
  
  return roles.includes(effectiveRole);
}

/**
 * Component that only renders children if user has required role
 */
export const RoleGate: React.FC<{
  children: React.ReactNode;
  minRole?: UserRole;
  allowedRoles?: UserRole[];
  fallback?: React.ReactNode;
}> = ({ children, minRole, allowedRoles, fallback = null }) => {
  const { userRole } = usePlatformTenant();
  const effectiveRole: UserRole = userRole || 'user';

  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(effectiveRole)) {
      return <>{fallback}</>;
    }
  } else if (minRole) {
    const userRoleLevel = ROLE_HIERARCHY[effectiveRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0;
    
    if (userRoleLevel < requiredLevel) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
};

export default RequireAuth;
